import { ContactInfoType, CustomerDetails, CustomerFormData, CustomerTableData } from '../types/CustomerTypes';
import {
  CreateCustomerMutationInput,
  Dtos_V2_Customer_CustomerDto,
  UpdateCustomerMutationInput
} from '@/common/infrastructure/gql/generated/schema';

// Helper function to compare contacts
const areContactsDifferent = (contact1: unknown, contact2: unknown) => {
  return JSON.stringify(contact1) !== JSON.stringify(contact2);
};

export const mapCustomerToFormData = (customer?: CustomerDetails | null): CustomerFormData => {
  if (!customer)
    return {
      id: undefined,
      companyName: '',
      primaryEmail: '',
      primaryPhone: '',
      customerTypeId: undefined,
      salesAgentId: undefined,
      termId: undefined,
      pricingSchemeId: undefined,
      salesRegionId: undefined,
      accountTypeId: undefined,
      customerDiscount: undefined,
      maxCredit: undefined,
      taxNumber: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      contacts: [],
      notes: ''
    };
  return {
    id: customer.id ?? undefined,
    companyName: customer.companyName ?? '',
    primaryEmail: customer.primaryEmail ?? '',
    primaryPhone: customer.primaryPhone ?? '',
    customerTypeId: customer.customerType?.id ?? undefined,
    salesAgentId: customer.salesAgent?.empId ? customer.salesAgent.empId : undefined,
    termId: customer.term?.id ?? undefined,
    pricingSchemeId: customer.pricingScheme?.id ?? undefined,
    salesRegionId: customer.salesRegion?.id ?? undefined,
    accountTypeId: customer.accountType?.id ?? undefined,
    customerDiscount: customer.customerDiscount ?? undefined,
    maxCredit: customer.maxCredit ?? undefined,
    taxNumber: customer.taxNumber ?? '',
    address: customer.address?.address ?? '',
    city: customer.address?.city ?? '',
    state: customer.address?.state ?? '',
    zipCode: customer.address?.zipCode ?? '',
    country: customer.address?.country ?? '',
    contacts: customer.contacts?.map((contact) => ({
      id: contact.id ?? undefined,
      firstName: contact.firstName ?? '',
      lastName: contact.lastName ?? '',
      title: contact.title ?? '',
      contactInformation: contact.contactInformation?.map((info) => ({
        id: info.id ?? undefined,
        type: info.type ? (info.type as ContactInfoType) : ContactInfoType.OTHER,
        contact: info.contact ?? ''
      }))
    })),
    notes: customer.notes ?? ''
  };
};

export const mapCustomerToTableData = (customer: Dtos_V2_Customer_CustomerDto): CustomerTableData => {
  return {
    id: customer.id ?? 0,
    companyName: customer.companyName ?? 'N/A',
    primaryEmail: customer.primaryEmail ?? 'N/A',
    primaryPhone: customer.primaryPhone ?? 'N/A',
    address: {
      address: customer.address?.address ?? 'N/A'
    },
    salesRegion: customer.salesRegion?.salesRegionName ?? 'N/A',
    salesAgent: customer.salesAgent?.fullName ?? 'N/A',
    customerType: customer.customerType?.typeName ?? 'N/A',
    maxCredit: customer.maxCredit ?? 0,
    customerStatus: customer.customerStatus ?? 'N/A',
    deletable: customer.deletable ?? false,
    financialSummary: customer.financialSummary
      ? {
          revenue: customer.financialSummary.revenue ?? 0
        }
      : undefined
  };
};

export const mapFormDataToCreateInput = (formData: CustomerFormData): CreateCustomerMutationInput => {
  return {
    ...(formData.companyName && { companyName: formData.companyName }),
    ...(formData.primaryEmail && { primaryEmail: formData.primaryEmail }),
    ...(formData.primaryPhone && { primaryPhone: formData.primaryPhone }),
    ...(formData.customerTypeId && { customerType: { id: formData.customerTypeId } }),
    ...(formData.salesAgentId && { salesAgent: { empId: Number(formData.salesAgentId) } }),
    ...(formData.termId && { term: { id: formData.termId } }),
    ...(formData.pricingSchemeId && { pricingScheme: { id: formData.pricingSchemeId } }),
    ...(formData.salesRegionId && { salesRegion: { id: formData.salesRegionId } }),
    ...(formData.accountTypeId && { accountType: { id: formData.accountTypeId } }),
    ...(formData.customerDiscount !== undefined && { customerDiscount: Number(formData.customerDiscount) }),
    ...(formData.maxCredit !== undefined && { maxCredit: Number(formData.maxCredit) }),
    ...(formData.taxNumber && { taxNumber: formData.taxNumber }),
    ...(formData.address &&
      formData.address.trim() && {
        address: {
          address: formData.address,
          city: formData.city || '',
          state: formData.state || '',
          zipCode: formData.zipCode || '',
          country: formData.country || ''
        }
      }),
    ...(formData.contacts &&
      formData.contacts.length > 0 && {
        contactsToCreate: formData.contacts.map((contact) => ({
          firstName: contact.firstName,
          lastName: contact.lastName,
          title: contact.title,
          contactInformation:
            contact.contactInformation?.map((info) => ({
              type: info.type,
              contact: info.contact
            })) || []
        }))
      }),
    ...(formData.notes && { notes: formData.notes })
  };
};

export const mapFormDataToUpdateInput = (formData: CustomerFormData, originalCustomer?: CustomerDetails): UpdateCustomerMutationInput => {
  if (!formData.id) {
    throw new Error('Customer ID is required for update');
  }

  const originalContacts = originalCustomer?.contacts || [];
  const currentContacts = formData.contacts || [];

  // Find new contacts (no ID)
  const contactsToCreate = currentContacts
    .filter((contact) => !contact.id)
    .map((contact) => ({
      firstName: contact.firstName,
      lastName: contact.lastName,
      title: contact.title,
      contactInformation:
        contact.contactInformation?.map((info) => ({
          type: info.type,
          contact: info.contact
        })) || []
    }));

  // Find updated contacts (has ID and different from original)
  const contactsToUpdate = currentContacts
    .filter((contact) => contact.id)
    .map((contact) => {
      const originalContact = originalContacts.find((oc) => oc.id === contact.id);
      if (originalContact && areContactsDifferent(originalContact, contact)) {
        return {
          id: contact.id,
          firstName: contact.firstName,
          lastName: contact.lastName,
          title: contact.title,
          contactInformation:
            contact.contactInformation?.map((info) => ({
              id: info?.id,
              type: info.type,
              contact: info.contact
            })) || []
        };
      }
      return null;
    })
    .filter((contact): contact is NonNullable<typeof contact> => contact !== null);

  // Find deleted contacts (in original but not in current)
  const contactsToDelete = originalContacts
    .filter((originalContact) => originalContact.id && !currentContacts.find((c) => c.id === originalContact.id))
    .map((contact) => contact.id!);

  // Helper function to handle clearable string fields
  const mapClearableStringField = (value: string | null | undefined): string | null => {
    if (value === null || value === undefined || value.trim() === '') {
      return null;
    }
    return value.trim();
  };

  // Helper function to handle clearable numeric/ID fields
  const mapClearableIdField = (value: number | null | undefined): number | null => {
    if (value === null || value === undefined || value === 0) {
      return null;
    }
    return value;
  };

  return {
    id: formData.id,
    // Handle clearable string fields - send null when cleared
    ...(formData.companyName !== undefined && { companyName: mapClearableStringField(formData.companyName) }),
    ...(formData.primaryEmail !== undefined && { primaryEmail: mapClearableStringField(formData.primaryEmail) }),
    ...(formData.primaryPhone !== undefined && { primaryPhone: mapClearableStringField(formData.primaryPhone) }),
    // Handle clearable ID fields - send null when cleared
    ...(formData.customerTypeId !== undefined && {
      customerType: mapClearableIdField(formData.customerTypeId) ? { id: formData.customerTypeId } : null
    }),
    ...(formData.salesAgentId !== undefined && {
      salesAgent: mapClearableIdField(formData.salesAgentId) ? { empId: Number(formData.salesAgentId) } : null
    }),
    ...(formData.termId !== undefined && {
      term: mapClearableIdField(formData.termId) ? { id: formData.termId } : null
    }),
    ...(formData.pricingSchemeId !== undefined && {
      pricingScheme: mapClearableIdField(formData.pricingSchemeId) ? { id: formData.pricingSchemeId } : null
    }),
    ...(formData.salesRegionId !== undefined && {
      salesRegion: mapClearableIdField(formData.salesRegionId) ? { id: formData.salesRegionId } : null
    }),
    ...(formData.accountTypeId !== undefined && {
      accountType: mapClearableIdField(formData.accountTypeId) ? { id: formData.accountTypeId } : null
    }),
    ...(formData.customerDiscount !== undefined && { customerDiscount: Number(formData.customerDiscount) }),
    ...(formData.maxCredit !== undefined && { maxCredit: Number(formData.maxCredit) }),
    ...(formData.taxNumber !== undefined && { taxNumber: mapClearableStringField(formData.taxNumber) }),
    // Address: send null when all address fields are empty
    ...(formData.address !== undefined && {
      address:
        formData.address.trim() || formData.city?.trim() || formData.state?.trim() || formData.zipCode?.trim() || formData.country?.trim()
          ? {
              address: formData.address.trim() || null,
              city: formData.city?.trim() || null,
              state: formData.state?.trim() || null,
              zipCode: formData.zipCode?.trim() || null,
              country: formData.country?.trim() || null
            }
          : null
    }),
    ...(contactsToCreate.length > 0 && { contactsToCreate }),
    ...(contactsToUpdate.length > 0 && { contactsToUpdate }),
    ...(contactsToDelete.length > 0 && { contactsToDelete }),
    ...(formData.notes !== undefined && { notes: mapClearableStringField(formData.notes) })
  };
};
